/**
 * Sales Portal API Client
 * ========================
 * API client for sales-assisted pre-onboarding flow
 *
 * Authentication:
 * - Uses separate sales JWT tokens (not restaurant tokens)
 * - Token stored in localStorage as 'sales_token'
 * - Token type: 'sales' (validated by backend)
 */

import axios from 'axios';

// Create dedicated axios instance for sales portal
export const salesApiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add sales auth token
salesApiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('sales_token');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
salesApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear and redirect
      localStorage.removeItem('sales_token');
      localStorage.removeItem('sales_user');
      window.location.href = '/sales-horse';
    }
    return Promise.reject(error);
  }
);

// =========================================
// TYPE DEFINITIONS
// =========================================

export interface SalesUser {
  sales_user_id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: 'sales' | 'admin';
  must_change_password?: boolean;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  token: string;
  user: SalesUser;
  message: string;
}

export interface DashboardStats {
  total_uploads: number;
  menus_extracted: number;
  invites_sent: number;
  activation_links_opened: number;
  conversions: number;
  conversion_rate: number;
  avg_processing_time: number | null;
}

export interface SubscriptionType {
  type: 'virtual_assistant' | 'virtual_host';
  plan?: string;
  status?: string;
}

export interface Session {
  session_id: string;
  restaurant_name: string;
  menu_item_count: number | null;
  status: 'pending' | 'activated' | 'expired' | 'ready' | 'processing' | 'failed';
  created_at: string;
  rest_id: number | null;
  rest_account_id?: number | null;
  invite_token?: string;
  activation_link_opened_count?: number;
  // Upsell data
  subscription_types?: SubscriptionType[];
  has_virtual_assistant?: boolean;
  has_virtual_host?: boolean;
  has_stripe_customer?: boolean;
}

export interface SessionsResponse {
  sessions: Session[];
  total: number;
  page: number;
  pages: number;
  limit: number;
}

export interface UploadRecord {
  id: number;
  session_id: string;
  restaurant_name: string;
  location: string;
  files: string[];
  file_count: number;
  file_size_mb: number;
  page_count: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  items_extracted: number;
  processing_time_seconds: number;
  sales_rep: string;
  source_type: string;
  created_at: string;
  claimed: boolean;
  claimed_at: string | null;
}

export interface UploadHistoryResponse {
  uploads: UploadRecord[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
  stats: {
    total_uploads: number;
    completed: number;
    failed: number;
    claimed: number;
    avg_items: number;
    avg_time_seconds: number;
  };
}

// =========================================
// AUTH API
// =========================================

export const salesApi = {
  /**
   * Login with username/email and password
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await salesApiClient.post<LoginResponse>('/sales/login', credentials);
    return response.data;
  },

  /**
   * Logout (client-side only - just clear tokens)
   */
  logout(): void {
    localStorage.removeItem('sales_token');
    localStorage.removeItem('sales_user');
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem('sales_token');
    return !!token;
  },

  /**
   * Get current user from localStorage
   */
  getCurrentUser(): SalesUser | null {
    const userJson = localStorage.getItem('sales_user');
    if (!userJson) return null;

    try {
      return JSON.parse(userJson);
    } catch {
      return null;
    }
  },

  // =========================================
  // DASHBOARD API
  // =========================================

  /**
   * Get dashboard statistics
   */
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await salesApiClient.get<DashboardStats>('/sales/dashboard-stats');
    return response.data;
  },

  /**
   * Get recent sessions for dashboard
   */
  async getRecentSessions(limit: number = 5): Promise<{ sessions: Session[] }> {
    const response = await salesApiClient.get<{ sessions: Session[] }>('/sales/recent-sessions', {
      params: { limit }
    });
    return response.data;
  },

  // =========================================
  // SESSIONS API
  // =========================================

  /**
   * Get paginated sessions list with filters
   */
  async getSessions(params: {
    page?: number;
    limit?: number;
    search?: string;
    filter_status?: string;
    sort?: string;
  } = {}): Promise<SessionsResponse> {
    const response = await salesApiClient.get<SessionsResponse>('/sales/sessions', { params });
    return response.data;
  },

  // =========================================
  // PREPARE DEMO API
  // =========================================

  /**
   * Prepare menu for demo (after extraction)
   */
  async prepareMenuForDemo(data: {
    session_id: string;
    restaurant_name: string;
    restaurant_phone?: string;
    restaurant_email?: string;
    restaurant_address?: string;
    sales_rep_user_id: number;
    sales_rep_email: string;
  }): Promise<any> {
    const response = await salesApiClient.post('/sales/prepare-menu-for-demo', data);
    return response.data;
  },

  /**
   * Generate activation invite link
   */
  async generateInvite(data: {
    session_id: string;
    restaurant_email: string;
    restaurant_name: string;
    sales_rep_name: string;
  }): Promise<any> {
    const response = await salesApiClient.post('/sales/generate-invite', data);
    return response.data;
  },

  // =========================================
  // USER MANAGEMENT API (ADMIN ONLY)
  // =========================================

  /**
   * Get all sales users (admin only)
   */
  async getUsers(): Promise<any[]> {
    const response = await salesApiClient.get('/sales/users');
    return response.data;
  },

  /**
   * Get single user by ID (admin only)
   */
  async getUser(userId: number): Promise<any> {
    const response = await salesApiClient.get(`/sales/users/${userId}`);
    return response.data;
  },

  /**
   * Create new sales user (admin only)
   */
  async createUser(data: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    role: 'sales' | 'admin';
    phone?: string;
    sales_territory?: string;
  }): Promise<any> {
    const response = await salesApiClient.post('/sales/users', data);
    return response.data;
  },

  /**
   * Update sales user (admin only)
   */
  async updateUser(userId: number, data: {
    email?: string;
    first_name?: string;
    last_name?: string;
    role?: 'sales' | 'admin';
    phone?: string;
    sales_territory?: string;
    is_active?: boolean;
  }): Promise<any> {
    const response = await salesApiClient.put(`/sales/users/${userId}`, data);
    return response.data;
  },

  /**
   * Deactivate user (admin only)
   */
  async deactivateUser(userId: number): Promise<any> {
    const response = await salesApiClient.delete(`/sales/users/${userId}`);
    return response.data;
  },

  /**
   * Activate user (admin only)
   */
  async activateUser(userId: number): Promise<any> {
    const response = await salesApiClient.post(`/sales/users/${userId}/activate`);
    return response.data;
  },

  /**
   * Reset user password (admin only)
   */
  async resetPassword(userId: number, newPassword: string): Promise<any> {
    const response = await salesApiClient.post(`/sales/users/${userId}/reset-password`, {
      new_password: newPassword
    });
    return response.data;
  },

  /**
   * Change current user's password (typically after first login)
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<any> {
    const response = await salesApiClient.post('/sales/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    });
    return response.data;
  },

  /**
   * Get admin access to restaurant dashboard (admin only)
   */
  async getAdminDashboardAccess(restAccountId: number): Promise<{
    success: boolean;
    dashboard_url: string;
    restaurant_name: string;
    expires_in_minutes: number;
    message: string;
  }> {
    const response = await salesApiClient.post('/sales/admin/access-dashboard', {
      rest_account_id: restAccountId
    });
    return response.data;
  },

  /**
   * Create upsell checkout for existing customer
   * Allows sales reps to add Virtual Host to VA customers or VA to VH customers
   * @param restAccountId - Restaurant account ID
   * @param productType - Product to add
   * @param planName - Optional specific plan name (e.g., 'standard_virtual_host')
   * @param billingCycle - Optional billing cycle ('monthly' or 'annual')
   */
  async createUpsellCheckout(
    restAccountId: number,
    productType: 'virtual_assistant' | 'virtual_host',
    planName?: string,
    billingCycle?: 'monthly' | 'annual'
  ): Promise<{
    success: boolean;
    checkout_url: string;
    session_id: string;
    product_type: string;
    message: string;
  }> {
    const response = await salesApiClient.post('/sales/create-upsell-checkout', {
      rest_account_id: restAccountId,
      product_type: productType,
      plan_name: planName,
      billing_cycle: billingCycle
    });
    return response.data;
  },

  // =========================================
  // UPLOAD HISTORY API
  // =========================================

  /**
   * Get upload history with filters and pagination
   */
  // =========================================
  // CALL ANALYZER API (Admin)
  // =========================================

  async getAnalyzerRestaurants(search: string = ''): Promise<any[]> {
    const response = await salesApiClient.get('/sales/call-analyzer/restaurants', {
      params: { search }
    });
    return response.data;
  },

  async getAnalyzerCalls(params: {
    rest_account_id: number;
    phone?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ total: number; calls: any[] }> {
    const response = await salesApiClient.get('/sales/call-analyzer/calls', { params });
    return response.data;
  },

  async getCallAnalysis(callId: number): Promise<any> {
    const response = await salesApiClient.get(`/sales/call-analyzer/calls/${callId}/analysis`);
    return response.data;
  },

  async saveAnnotation(callId: number, data: { comments: string; tags: string[] }): Promise<any> {
    const response = await salesApiClient.post(`/sales/call-analyzer/calls/${callId}/annotate`, data);
    return response.data;
  },

  async getAnnotations(params?: {
    rest_account_id?: number;
    tag?: string;
    limit?: number;
    offset?: number;
  }): Promise<any[]> {
    const response = await salesApiClient.get('/sales/call-analyzer/annotations', { params });
    return response.data;
  },

  // =========================================
  // UPLOAD HISTORY API
  // =========================================

  async getUploadHistory(params: {
    page?: number;
    limit?: number;
    status_filter?: string;
    sales_rep_filter?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<UploadHistoryResponse> {
    const response = await salesApiClient.get<UploadHistoryResponse>('/sales/upload-history', {
      params
    });
    return response.data;
  },
};
